import React, { useEffect, useMemo, useRef, useState } from 'react';
import CurveChart from './CurveChart';
import CurveSpark from './CurveSpark';
import { resolveLabel, fmtDate, fmtV, drawRowState } from './curveFormat';

const SLEEPERCDN = 'https://sleepercdn.com/content/nfl/players/thumb/';

/* Your previous curves, one surface, two shapes:
   — mobile (no rail): record strip + searchable rows, tap to unfold the chart
   — desktop: a single viewer card; the rail is the index that drives it */
const styles = `
  .cvh { margin-top: 34px; }
  .cvh-agg { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 22px; }
  .cvh-stat { background: var(--surface); border-radius: 12px; padding: 13px 14px; }
  .cvh-stat .n { font-size: 22px; font-weight: 700; letter-spacing: -.02em; }
  .cvh-stat .n small { font-size: 12px; color: var(--ink3); font-weight: 500; }
  .cvh-stat .k { font-family: var(--mono); font-size: 8.5px; font-weight: 700; letter-spacing: .12em;
    color: var(--ink3); margin-top: 4px; }
  .cvh-stat.hero { background: #1D1D1F; color: #fff; }
  .cvh-stat.hero .n { color: var(--brand); }
  .cvh-stat.hero .k { color: rgba(255,255,255,.5); }

  .cvh-k { display: flex; align-items: baseline; font-family: var(--mono); font-size: 10px; font-weight: 700;
    letter-spacing: .22em; color: var(--ink3); margin: 24px 0 12px; }
  .cvh-k .r { margin-left: auto; letter-spacing: .08em; font-weight: 600; }

  .cvh-search { display: flex; align-items: center; gap: 8px; background: var(--surface);
    border-radius: 10px; padding: 10px 12px; margin-bottom: 6px; }
  .cvh-search svg { width: 13px; height: 13px; color: var(--ink3); flex: none; }
  .cvh-search input { border: none; background: none; outline: none; font: inherit; font-size: 15px;
    color: var(--ink); width: 100%; }
  .cvh-search input::placeholder { color: var(--ink3); }

  .cvh-row-wrap { border-bottom: 1px solid var(--line); }
  .cvh-row-wrap:last-child { border-bottom: none; }
  .cv-page .cvh-row { display: flex; align-items: center; gap: 11px; padding: 11px 4px; width: 100%;
    text-align: left; border-radius: 12px; transition: background .2s var(--ease); }
  .cv-page .cvh-row:active { background: var(--surface); }
  .cvh-row .ava { width: 40px; height: 40px; border-radius: 50%; background: #D2D2D7 center/cover no-repeat; flex: none; }
  .cvh-row .who { min-width: 0; }
  .cvh-row .nm { font-size: 15px; font-weight: 700; letter-spacing: -.01em; white-space: nowrap;
    overflow: hidden; text-overflow: ellipsis; }
  .cvh-row .mt { font-family: var(--mono); font-size: 9px; color: var(--ink3); margin-top: 3px; letter-spacing: .05em; }
  .cvh-row .spark { margin-left: auto; }
  .cvh-chip { font-family: var(--mono); font-size: 10.5px; font-weight: 700; letter-spacing: .05em;
    padding: 4px 10px; border-radius: 980px; flex: none; }
  .cvh-chip.track { background: var(--green-soft); color: var(--green-ink); }
  .cvh-chip.fit { background: #1D1D1F; color: var(--brand); }
  .cvh-chip.today { background: var(--brand-soft); color: var(--brand-ink); font-size: 9px; }
  .cvh-chip.none { visibility: hidden; padding: 4px 0; }
  .cvh-unfold { padding: 2px 0 14px; }

  .cvh-card { background: #fff; border: 1px solid var(--line); border-radius: 16px; overflow: hidden;
    box-shadow: 0 10px 30px rgba(0,0,0,.06); }
  .cvh-card.flash { animation: cvhflash 1.1s var(--ease); }
  @keyframes cvhflash { 0% { box-shadow: 0 0 0 3px var(--brand); } 100% { box-shadow: 0 10px 30px rgba(0,0,0,.06); } }
  .cvh-card-head { display: flex; align-items: center; gap: 11px; padding: 13px 16px 9px; }
  .cvh-ava { width: 38px; height: 38px; border-radius: 50%; background: #D2D2D7; overflow: hidden; flex: none;
    display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 700; font-size: 12px; position: relative; }
  .cvh-ava img { width: 100%; height: 100%; object-fit: cover; position: absolute; inset: 0; }
  .cvh-nm { font-size: 16px; font-weight: 700; letter-spacing: -.01em; }
  .cvh-mt { font-family: var(--mono); font-size: 10px; color: var(--ink3); margin-top: 2px; letter-spacing: .04em; }
  .cvh-headchip { margin-left: auto; }
  .cvh-card-chart { padding: 0 8px; }
  .cvh-card-foot { display: flex; gap: 8px; align-items: center; padding: 8px 16px 13px; flex-wrap: wrap; }
  .cvh-tag { font-family: var(--mono); font-size: 9.5px; font-weight: 700; letter-spacing: .08em;
    padding: 3px 9px; border-radius: 980px; background: var(--surface); color: var(--ink2); }
  .cvh-tag.beat { background: var(--brand-soft); color: var(--brand-ink); }
  .cvh-hint { font-size: 12.5px; color: var(--ink3); text-align: center; margin-top: 10px; }
  .cvh-empty { font-size: 13px; color: var(--ink3); padding: 10px 4px; }

  @media (max-width: 600px) {
    .cvh-agg { grid-template-columns: repeat(2, 1fr); }
  }
`;

function chipEl(draw, todayLineId, className = 'cvh-chip') {
  const { chip } = drawRowState(draw, todayLineId);
  if (!chip) return <span className={`${className} none`}>0</span>;
  return <span className={`${className} ${chip.kind}`}>{chip.label}</span>;
}

function DrawCard({ draw, showHead = true, flash = false }) {
  const line = draw.line;
  const settled = line.status === 'settled';
  const marksSoFar = (line.marks || []).filter((m) => m.value != null).length;
  return (
    <div className={`cvh-card${flash ? ' flash' : ''}`}>
      {showHead ? (
        <div className="cvh-card-head">
          <span className="cvh-ava">
            {line.player.sleeperId ? (
              <img src={`${SLEEPERCDN}${line.player.sleeperId}.jpg`} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : null}
            {(line.player.name || '?').slice(0, 2).toUpperCase()}
          </span>
          <div>
            <div className="cvh-nm">{line.player.name}</div>
            <div className="cvh-mt">
              DRAWN {fmtDate(line.lineDate)} · {settled ? `SETTLED ${fmtDate(line.resolveDate)}` : `SETTLES ${resolveLabel(line.resolveDate).toUpperCase()}`}
            </div>
          </div>
          <span className="cvh-headchip">{chipEl(draw, null)}</span>
        </div>
      ) : null}
      <div className="cvh-card-chart">
        <CurveChart
          history={line.history}
          startValue={line.startValue}
          horizonWeeks={line.horizonWeeks}
          band={line.band}
          marks={line.marks}
          startDate={line.startDate}
          myPoints={draw.points}
          endLabel={fmtDate(line.resolveDate)}
        />
      </div>
      <div className="cvh-card-foot">
        <span className="cvh-tag">YOUR CALL {fmtV(draw.points[draw.points.length - 1])}</span>
        {marksSoFar > 0 && !settled ? <span className="cvh-tag">GRADED {marksSoFar} {marksSoFar === 1 ? 'DAY' : 'DAYS'}</span> : null}
        {settled && draw.beatBand ? <span className="cvh-tag beat">BEAT CONSENSUS</span> : null}
        {settled && draw.percentile != null ? <span className="cvh-tag">BEAT {Math.round(draw.percentile)}% OF CALLS</span> : null}
      </div>
    </div>
  );
}

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
  </svg>
);

function MobileRow({ draw, todayLineId, expanded, onToggle }) {
  const line = draw.line;
  const { meta } = drawRowState(draw, todayLineId);
  return (
    <div className="cvh-row-wrap">
      <button type="button" className="cvh-row" onClick={onToggle}>
        <span className="ava" style={line.player.sleeperId ? { backgroundImage: `url(${SLEEPERCDN}${line.player.sleeperId}.jpg)` } : undefined} />
        <span className="who">
          <span className="nm" style={{ display: 'block' }}>{line.player.name}</span>
          <span className="mt" style={{ display: 'block' }}>{meta}</span>
        </span>
        <span className="spark">
          <CurveSpark points={draw.points} marks={line.marks} startValue={line.startValue}
            startDate={line.startDate} horizonWeeks={line.horizonWeeks} />
        </span>
        {chipEl(draw, todayLineId)}
      </button>
      {expanded ? (
        <div className="cvh-unfold">
          <DrawCard draw={draw} showHead={false} />
        </div>
      ) : null}
    </div>
  );
}

export default function CurveHistory({ tape, isMobile, focus, todayLineId }) {
  const [q, setQ] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const viewerRef = useRef(null);

  const draws = useMemo(
    () => (tape && Array.isArray(tape.draws) ? tape.draws.filter((d) => d.line.id !== todayLineId) : []),
    [tape, todayLineId]
  );

  // Desktop viewer: the rail's pick, else your freshest open line.
  const focused = useMemo(() => {
    if (focus && focus.id) {
      const hit = draws.find((d) => d.id === focus.id);
      if (hit) return hit;
    }
    return draws.find((d) => d.line.status !== 'settled') || draws[0] || null;
  }, [draws, focus]);

  useEffect(() => {
    if (focus && focus.tick && viewerRef.current) {
      viewerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focus]);

  if (!draws.length) return null;

  const needle = q.trim().toLowerCase();
  const filtered = needle ? draws.filter((d) => (d.line.player.name || '').toLowerCase().includes(needle)) : draws;
  const open = filtered.filter((d) => d.line.status !== 'settled');
  const settled = filtered.filter((d) => d.line.status === 'settled');
  const allSettled = draws.filter((d) => d.line.status === 'settled');
  const beats = allSettled.filter((d) => d.beatBand).length;

  if (!isMobile) {
    return (
      <div className="cvh">
        <style>{styles}</style>
        <div className="cvh-k">
          <span>YOUR LINES</span>
          <span className="r">{draws.length - allSettled.length} OPEN · {allSettled.length} SETTLED</span>
        </div>
        {focused ? (
          <div ref={viewerRef}>
            <DrawCard key={`${focused.id}-${focus ? focus.tick : 0}`} draw={focused} flash={!!(focus && focus.tick)} />
          </div>
        ) : null}
        {draws.length > 1 ? <div className="cvh-hint">Pick any line in the sidebar.</div> : null}
      </div>
    );
  }

  return (
    <div className="cvh">
      <style>{styles}</style>
      <div className="cvh-agg">
        <div className="cvh-stat hero">
          <div className="n">{tape.accuracy != null ? Math.round(tape.accuracy) : '—'}</div>
          <div className="k">{tape.gradedCount ? `ACCURACY · ${tape.gradedCount} GRADED` : 'ACCURACY · FIRST GRADE TONIGHT'}</div>
        </div>
        <div className="cvh-stat"><div className="n">🔥 {tape.streak}</div><div className="k">DAY STREAK</div></div>
        <div className="cvh-stat"><div className="n">{draws.length - allSettled.length}</div><div className="k">OPEN LINES</div></div>
        <div className="cvh-stat">
          <div className="n">{allSettled.length ? `${beats}` : '—'}<small>{allSettled.length ? ` of ${allSettled.length}` : ''}</small></div>
          <div className="k">BEAT CONSENSUS</div>
        </div>
      </div>

      {draws.length > 2 ? (
        <div className="cvh-search">
          <SearchIcon />
          <input placeholder="Find a player…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      ) : null}

      {open.length > 0 ? (
        <>
          <div className="cvh-k"><span>OPEN — REALITY IS DRAWING</span></div>
          {open.map((d) => (
            <MobileRow key={d.id} draw={d} todayLineId={todayLineId} expanded={expandedId === d.id}
              onToggle={() => setExpandedId((cur) => (cur === d.id ? null : d.id))} />
          ))}
        </>
      ) : null}

      {settled.length > 0 ? (
        <>
          <div className="cvh-k"><span>SETTLED — THE RECEIPTS</span></div>
          {settled.map((d) => (
            <MobileRow key={d.id} draw={d} todayLineId={todayLineId} expanded={expandedId === d.id}
              onToggle={() => setExpandedId((cur) => (cur === d.id ? null : d.id))} />
          ))}
        </>
      ) : null}

      {needle && !open.length && !settled.length ? <div className="cvh-empty">No line on him yet.</div> : null}
    </div>
  );
}
